import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Workflow, Plus, Edit, Trash2, Check, X, Zap, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRIGGER_TYPES = [
  { value: 'stage_change', label: 'Stage Change', icon: '🔄' },
  { value: 'time_based', label: 'Time Based', icon: '⏰' },
  { value: 'score_threshold', label: 'Score Threshold', icon: '📊' },
  { value: 'assignment', label: 'Assignment', icon: '👤' },
  { value: 'no_activity', label: 'No Activity', icon: '⚠️' },
  { value: 'custom', label: 'Custom', icon: '⚙️' },
];

const WorkflowManager = () => {
  const { getAuthHeader, admin } = useAuth();
  const [workflowTriggers, setWorkflowTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'stage_change',
    is_active: true,
    conditions: {},
    actions: [],
    priority: 0
  });

  useEffect(() => {
    fetchWorkflowTriggers();
  }, []);

  const fetchWorkflowTriggers = async () => {
    try {
      const response = await axios.get(`${API}/admin/workflow-triggers`, {
        headers: getAuthHeader()
      });
      setWorkflowTriggers(response.data);
    } catch (error) {
      console.error('Error fetching workflow triggers:', error);
      alert('Failed to fetch workflow triggers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrigger = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/workflow-triggers`, formData, {
        headers: getAuthHeader()
      });
      setIsCreateDialogOpen(false);
      resetForm();
      fetchWorkflowTriggers();
      alert('Workflow trigger created successfully!');
    } catch (error) {
      console.error('Error creating workflow trigger:', error);
      alert('Failed to create workflow trigger');
    }
  };

  const handleEditTrigger = (trigger) => {
    setEditingTrigger(trigger);
    setFormData({
      name: trigger.name,
      description: trigger.description || '',
      trigger_type: trigger.trigger_type,
      is_active: trigger.is_active,
      conditions: trigger.conditions || {},
      actions: trigger.actions || [],
      priority: trigger.priority || 0
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateTrigger = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/workflow-triggers/${editingTrigger.id}`, formData, {
        headers: getAuthHeader()
      });
      setIsEditDialogOpen(false);
      setEditingTrigger(null);
      resetForm();
      fetchWorkflowTriggers();
      alert('Workflow trigger updated successfully!');
    } catch (error) {
      console.error('Error updating workflow trigger:', error);
      alert('Failed to update workflow trigger');
    }
  };

  const handleDeleteTrigger = async (triggerId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this workflow trigger?');
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API}/admin/workflow-triggers/${triggerId}`, {
        headers: getAuthHeader()
      });
      fetchWorkflowTriggers();
      alert('Workflow trigger deleted successfully!');
    } catch (error) {
      console.error('Error deleting workflow trigger:', error);
      alert('Failed to delete workflow trigger');
    }
  };

  const handleToggleActive = async (trigger) => {
    try {
      await axios.put(`${API}/admin/workflow-triggers/${trigger.id}`, {
        ...trigger,
        is_active: !trigger.is_active
      }, {
        headers: getAuthHeader()
      });
      fetchWorkflowTriggers();
    } catch (error) {
      console.error('Error toggling workflow trigger:', error);
      alert('Failed to toggle workflow trigger');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_type: 'stage_change',
      is_active: true,
      conditions: {},
      actions: [],
      priority: 0
    });
  };

  const getTriggerTypeInfo = (type) => {
    return TRIGGER_TYPES.find(t => t.value === type) || TRIGGER_TYPES[0];
  };

  if (loading) {
    return (
      <div className="workflow-manager">
        <div className="workflow-header">
          <h1><Workflow size={24} /> Workflow Automation</h1>
        </div>
        <div className="text-center py-8">Loading workflow triggers...</div>
      </div>
    );
  }

  return (
    <div className="workflow-manager">
      <div className="workflow-header">
        <div>
          <h1>
            <Workflow size={28} /> Workflow Automation
          </h1>
          <p className="text-muted-foreground">Automate repetitive tasks and follow-ups with intelligent triggers</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="scoring-btn-create">
              <Plus size={20} /> Create Workflow Trigger
            </Button>
          </DialogTrigger>
          <DialogContent className="scoring-dialog">
            <DialogHeader>
              <DialogTitle>Create New Workflow Trigger</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTrigger}>
              <div className="space-y-4">
                <div>
                  <Label>Trigger Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Send welcome email on new lead"
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What does this trigger do?"
                  />
                </div>
                <div>
                  <Label>Trigger Type *</Label>
                  <Select
                    value={formData.trigger_type}
                    onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIGGER_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <Label>Conditions (JSON)</Label>
                  <Input
                    value={JSON.stringify(formData.conditions)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, conditions: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    placeholder='{"stage": "new"}'
                  />
                  <p className="text-xs text-muted-foreground mt-1">e.g., {'{"stage": "qualified"}'} or {'{"hours_since_assignment": 24}'}</p>
                </div>
                <div>
                  <Label>Actions (JSON Array)</Label>
                  <Input
                    value={JSON.stringify(formData.actions)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, actions: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    placeholder='[{"action": "create_task", "task_type": "follow_up_call"}]'
                  />
                  <p className="text-xs text-muted-foreground mt-1">e.g., [{'{'}'"action": "send_email", "template": "welcome"'{'}'}]</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is-active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <Label htmlFor="is-active" className="cursor-pointer">
                    <Zap size={16} className="inline" /> Activate Trigger Immediately
                  </Label>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Trigger</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Workflow Trigger Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle><Edit size={24} className="inline mr-2" /> Edit Workflow Trigger</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateTrigger}>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Trigger Name *</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., New Lead - 24h Follow-up"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What does this trigger do?"
                  />
                </div>
                <div>
                  <Label>Trigger Type *</Label>
                  <Select
                    value={formData.trigger_type}
                    onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIGGER_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <Label>Conditions (JSON)</Label>
                  <Input
                    value={JSON.stringify(formData.conditions)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, conditions: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    placeholder='{"stage": "new"}'
                  />
                  <p className="text-xs text-muted-foreground mt-1">e.g., {'{"stage": "qualified"}'} or {'{"hours_since_assignment": 24}'}</p>
                </div>
                <div>
                  <Label>Actions (JSON Array)</Label>
                  <Input
                    value={JSON.stringify(formData.actions)}
                    onChange={(e) => {
                      try {
                        setFormData({ ...formData, actions: JSON.parse(e.target.value) });
                      } catch {}
                    }}
                    placeholder='[{"action": "create_task", "task_type": "follow_up_call"}]'
                  />
                  <p className="text-xs text-muted-foreground mt-1">e.g., [{'{'}'"action": "send_email", "template": "welcome"'{'}'}]</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-is-active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <Label htmlFor="edit-is-active" className="cursor-pointer">
                    <Zap size={16} className="inline" /> Active
                  </Label>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingTrigger(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit">Update Trigger</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Workflow Triggers List */}
      <div className="scoring-rules-grid">
        {workflowTriggers.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Workflow size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No Workflow Triggers Created</h3>
              <p className="text-muted-foreground mb-4">Create your first trigger to automate lead management</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus size={20} /> Create First Trigger
              </Button>
            </CardContent>
          </Card>
        ) : (
          workflowTriggers.map((trigger) => {
            const typeInfo = getTriggerTypeInfo(trigger.trigger_type);
            return (
              <Card key={trigger.id} className={`scoring-rule-card ${!trigger.is_active ? 'opacity-60' : ''}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{typeInfo.icon}</span>
                        <CardTitle className="text-lg">{trigger.name}</CardTitle>
                        {trigger.is_active && (
                          <span className="scoring-active-pill">
                            <Check size={12} /> Active
                          </span>
                        )}
                      </div>
                      <CardDescription>{trigger.description || 'No description provided'}</CardDescription>
                    </div>
                  </div>
                  <div className="scoring-category-badge bg-purple-100 text-purple-800 mt-2 inline-block">
                    {typeInfo.label}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-muted-foreground">Priority:</span> {trigger.priority}
                    </div>
                    <div>
                      <span className="font-semibold text-muted-foreground">Actions:</span> {trigger.actions?.length || 0} configured
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditTrigger(trigger)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <Edit size={16} /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(trigger)}
                      className={trigger.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}
                    >
                      {trigger.is_active ? <X size={16} /> : <Check size={16} />}
                      {trigger.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTrigger(trigger.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkflowManager;
